import React, { useEffect, useMemo, useState } from 'react';
import { FaApple, FaMobileAlt, FaShoppingBag, FaTimes, FaCheckCircle } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';

import './InstallAppPrompt.css';

const INSTALL_PROMPT_STORAGE_KEY = 'glace-install-prompt-v1';
const MIN_ROUTE_VISITS = 2;
const CTA_DISMISS_COOLDOWN_MS = 4 * 24 * 60 * 60 * 1000;
const NATIVE_DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;
const CTA_LINGER_DELAY_MS = 14000;

const createDefaultInstallState = () => ({
  dismissedUntil: 0,
  routeVisits: 0,
  lastSeenPath: '',
});

const readStoredInstallState = () => {
  if (typeof window === 'undefined') {
    return createDefaultInstallState();
  }

  try {
    const rawValue = window.localStorage.getItem(INSTALL_PROMPT_STORAGE_KEY);

    if (!rawValue) {
      return createDefaultInstallState();
    }

    const parsedValue = JSON.parse(rawValue);

    return {
      dismissedUntil: Number(parsedValue?.dismissedUntil) || 0,
      routeVisits: Number(parsedValue?.routeVisits) || 0,
      lastSeenPath: String(parsedValue?.lastSeenPath || ''),
    };
  } catch (error) {
    return createDefaultInstallState();
  }
};

const persistInstallState = (nextState) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(INSTALL_PROMPT_STORAGE_KEY, JSON.stringify(nextState));
};

const clearStoredInstallState = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(INSTALL_PROMPT_STORAGE_KEY);
};

const isIosDevice = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const touchPoints = Number(navigator.maxTouchPoints || 0);

  return /iPad|iPhone|iPod/.test(userAgent) || (platform === 'MacIntel' && touchPoints > 1);
};

const isStandaloneDisplayMode = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return Boolean(
    window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator?.standalone === true,
  );
};

function InstallAppPrompt({ cartItemsCount = 0 }) {
  const location = useLocation();
  const [installState, setInstallState] = useState(() => readStoredInstallState());
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(() => isStandaloneDisplayMode());
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false);
  const [hasLingered, setHasLingered] = useState(false);
  const isIos = useMemo(() => isIosDevice(), []);

  useEffect(() => {
    setInstallState((current) => {
      if (current.lastSeenPath === location.pathname) {
        return current;
      }

      const nextState = {
        ...current,
        lastSeenPath: location.pathname,
        routeVisits: current.routeVisits + 1,
      };

      persistInstallState(nextState);
      return nextState;
    });
  }, [location.pathname]);

  useEffect(() => {
    if (isInstalled || typeof window === 'undefined') {
      return undefined;
    }

    setHasLingered(false);

    const timeoutId = window.setTimeout(() => {
      setHasLingered(true);
    }, CTA_LINGER_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isInstalled, location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setIsInstructionsOpen(false);
      clearStoredInstallState();
    };

    const mediaQuery = window.matchMedia?.('(display-mode: standalone)');
    const handleDisplayModeChange = () => {
      if (isStandaloneDisplayMode()) {
        handleAppInstalled();
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    mediaQuery?.addEventListener?.('change', handleDisplayModeChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      mediaQuery?.removeEventListener?.('change', handleDisplayModeChange);
    };
  }, []);

  useEffect(() => {
    if (isInstalled) {
      clearStoredInstallState();
    }
  }, [isInstalled]);

  const dismissUntil = installState.dismissedUntil;
  const isCoolingDown = dismissUntil > Date.now();
  const engagementReached =
    hasLingered || Number(cartItemsCount || 0) > 0 || installState.routeVisits >= MIN_ROUTE_VISITS;
  const canPromptInstall = Boolean(deferredPrompt);
  const shouldRender = !isInstalled && !isCoolingDown && engagementReached && (canPromptInstall || isIos);

  const dismissCta = (cooldownMs) => {
    const nextState = {
      ...installState,
      dismissedUntil: Date.now() + cooldownMs,
    };

    persistInstallState(nextState);
    setInstallState(nextState);
    setIsInstructionsOpen(false);
  };

  const handlePrimaryAction = async () => {
    if (!canPromptInstall) {
      setIsInstructionsOpen((current) => !current);
      return;
    }

    try {
      await deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;

      setDeferredPrompt(null);

      if (choiceResult?.outcome !== 'accepted') {
        dismissCta(NATIVE_DISMISS_COOLDOWN_MS);
      }
    } catch (error) {
      console.error('N\u00e3o foi poss\u00edvel abrir o prompt de instala\u00e7\u00e3o:', error);
    }
  };

  if (!shouldRender) {
    return null;
  }

  const title = Number(cartItemsCount || 0) > 0
    ? 'Instale o app e volte direto para o seu pedido'
    : 'Instale a Glacê e abra o card\u00e1pio em um toque';
  const description = Number(cartItemsCount || 0) > 0
    ? 'Seu carrinho continua por perto para retomar o pedido mais r\u00e1pido, sem procurar a aba do navegador de novo.'
    : 'Tenha uma experi\u00eancia mais pr\u00e1tica para ver o card\u00e1pio, escolher presentes e seguir para o WhatsApp com menos atrito.';
  const badgeLabel = canPromptInstall ? 'Instalar app' : 'Adicionar \u00e0 tela inicial';
  const primaryActionLabel = canPromptInstall
    ? 'Instalar app'
    : isInstructionsOpen
      ? 'Ocultar passos'
      : 'Ver como instalar';

  return (
    <aside className="install-cta" aria-label={'Instala\u00e7\u00e3o do app da Glace'}>
      <button
        type="button"
        className="install-cta__close"
        onClick={() => dismissCta(CTA_DISMISS_COOLDOWN_MS)}
        aria-label={'Fechar convite de instala\u00e7\u00e3o'}
      >
        <FaTimes aria-hidden="true" />
      </button>

      <div className="install-cta__eyebrow">
        {isIos ? <FaApple aria-hidden="true" /> : <FaCheckCircle aria-hidden="true" />}
        <span>{badgeLabel}</span>
      </div>

      <h2 className="install-cta__title">{title}</h2>
      <p className="install-cta__description">{description}</p>

      <div className="install-cta__benefits" aria-hidden="true">
        <span>
          <FaMobileAlt />
          {'Acesso r\u00e1pido'}
        </span>
        <span>
          <FaShoppingBag />
          Carrinho por perto
        </span>
        <span>
          <FaCheckCircle />
          Janela mais limpa
        </span>
      </div>

      <div className="install-cta__actions">
        <button type="button" className="btn btn-primary" onClick={handlePrimaryAction}>
          {primaryActionLabel}
        </button>
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => dismissCta(CTA_DISMISS_COOLDOWN_MS)}
        >
          {'Agora n\u00e3o'}
        </button>
      </div>

      {!canPromptInstall && isIos && isInstructionsOpen ? (
        <div className="install-cta__instructions">
          <p>{'No iPhone ou iPad, a instala\u00e7\u00e3o acontece pelo menu de compartilhamento do Safari:'}</p>
          <ol>
            <li>Toque em Compartilhar na barra do navegador.</li>
            <li>{'Escolha Adicionar \u00e0 Tela de In\u00edcio.'}</li>
            <li>Confirme em Adicionar para salvar a Glace como app.</li>
          </ol>
        </div>
      ) : null}
    </aside>
  );
}

export default InstallAppPrompt;
